import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Collapse } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import { useConsultation } from "../../ConsultationContext";
import {
  findSystemBySubId,
  ENERGY_TRAINING_SUB_ID,
  TIER_COLOR,
  isSystemApproved,
} from "./systemMapping";
import { LikelyExperiencingItem } from "../../data/consultationTypes";
import {
  getCategoryColor,
  SCORE_CATEGORY_LABELS,
  enPrecisionTestKind,
} from "../../../platform/store/helper";
import { RootStatePlatform } from "../../../platform/store/platform-slice";
import { TimelineGroup } from "../../../platform/components/score/SidePanelShared";
import { isServiceConnectedToSystem } from "../../../platform/components/score/RecommendedServicesTab";
import {
  MetricCard,
  BloodBiomarkerCard,
} from "../../../platform/components/score/SystemDetails";
import {
  ServicesPage,
  ServiceContent,
} from "../../../new-reports/types/newReportTypes";
import EnergyTrainingPanel from "./EnergyTrainingPanel";
import HealthScoreBar from "./HealthScoreBar";
import ColoredDot from "../shared/ColoredDot";

// Consultation step 2 only renders blood biomarkers of this test kind
// (4 = "Body composition" in `enPrecisionTestKind`).
const BLOOD_BIOMARKER_TEST_KIND = 4;

const SystemDetailPanel: React.FC = () => {
  const theme: Theme = useTheme();
  const { data, stepper, aiActive } = useConsultation();
  const subId = stepper.current.sub.id;

  // "What you are likely experiencing" accordions: which items are expanded.
  // Multiple can be open at once. Collapsed by default (titles only) to keep
  // the panel short — except in AI-guided mode, where the full text always
  // shows (see render below). Reset when the system changes.
  const [openLikely, setOpenLikely] = useState<Set<number>>(new Set());
  const toggleLikely = (index: number) =>
    setOpenLikely((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  // Reset the panel's internal scroll on every system switch. The panel is
  // its own scroll container (`overflow: auto`) and React re-renders this
  // component in-place when the sidebar changes systems, so without this
  // the user lands mid-way down a different system's content.
  const panelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    panelRef.current?.scrollTo({ top: 0, behavior: "auto" });
    setOpenLikely(new Set());
  }, [subId]);

  // Energy & Training has its own panel (GetConsultation `energyTraining.zones`).
  if (subId === ENERGY_TRAINING_SUB_ID) {
    return <EnergyTrainingPanel />;
  }

  const system = findSystemBySubId(data.systems, subId);

  if (!system) {
    return (
      <Box sx={styles.panel(theme)}>
        <Box sx={styles.title(theme)}>System not available</Box>
        <Box sx={styles.subtle(theme)}>
          This system isn&apos;t scored for the current client&apos;s
          assessments.
        </Box>
      </Box>
    );
  }

  const score = typeof system.score === "number" ? system.score : 0;
  const scoreCategory =
    typeof system.scoreCategory === "number" ? system.scoreCategory : 2;
  const scoreColor = getCategoryColor("%", score, theme, scoreCategory);

  // Mirror the score screen: a system that isn't fully approved (approval !==
  // 4) hides its score + bar. We show the name only and skip the
  // score-centered TimelineGroup; the rest of the panel (recommended services,
  // likely-experiencing, metrics) still renders.
  const approved = isSystemApproved(system);

  // System runtime shape (the static type is stale): both `influencedBy` and
  // `affectsThis` are `{ positive, negative }` blocks of `{ label, description }`.
  // Backend ships these on every system row.
  const influencedBy = system.influencedBy;
  const affectsItems = system.affectsThis;

  // `likelyExperiencing` is shipped by the backend on each system row.
  const likely: LikelyExperiencingItem[] =
    (system as any).likelyExperiencing ?? [];

  // Recommended services — same logic as the score screen's right-pane tab.
  // Filters the prescriptionPlan services by `isServiceConnectedToSystem`,
  // which matches the service's `rational` rules against this system's
  // breath/blood biomarker ids.
  const servicesPage = (data.prescriptionPlan ?? []).find(
    (p: any) => p.pageType === "services"
  ) as ServicesPage | undefined;
  const recommendedServices: ServiceContent[] = (
    servicesPage?.services ?? []
  ).filter((svc) => isServiceConnectedToSystem(svc, system));

  return (
    <Box ref={panelRef} sx={styles.panel(theme)}>
      {/* 1. Title + score + HealthScoreBar. Unapproved systems show the name
          only (no score, no bar) — same as the score screen's SystemDetails. */}
      <Box sx={styles.titleSection}>
        <Box sx={styles.titleRow}>
          <Box component="span" sx={styles.title(theme)}>
            {system.name}
          </Box>
          {approved && (
            <Box component="span" sx={styles.scoreText(theme)}>
              {score}%
            </Box>
          )}
        </Box>
        {approved && (
          <HealthScoreBar score={score} scoreCategory={scoreCategory} />
        )}
      </Box>

      <Box sx={styles.divider(theme)} />

      {/* 2. Recommended services — filtered from prescriptionPlan by the same
          rule that the score screen's RecommendedServicesTab uses. */}
      {recommendedServices.length > 0 && (
        <Box sx={styles.section}>
          <Box sx={styles.sectionTitle(theme)}>Recommended services</Box>
          <Box sx={styles.serviceChips}>
            {recommendedServices.map((svc) => (
              <Box key={svc.serviceName} sx={styles.serviceChip(theme)}>
                {svc.serviceName}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* 3. TimelineGroup — Influenced by / Score / Affects this with vertical
          connector. `readOnly` strips the click-to-expand chip affordance:
          descriptions aren't authored for the consultation surface, so the
          click would only reveal an empty popup. For unapproved systems the
          middle score node is omitted (`hideScore`) so "Influenced by" and
          "Affects this" still render without exposing the hidden score. */}
      {/*<TimelineGroup*/}
      {/*  theme={theme}*/}
      {/*  influencedBy={influencedBy}*/}
      {/*  score={score}*/}
      {/*  scoreUnit="%"*/}
      {/*  scoreCategory={scoreCategory}*/}
      {/*  categoryColor={scoreColor}*/}
      {/*  categoryLabel={SCORE_CATEGORY_LABELS[scoreCategory] ?? ""}*/}
      {/*  affectsItems={affectsItems}*/}
      {/*  readOnly*/}
      {/*  hideScore={!approved}*/}
      {/*/>*/}

      {/* 4. What you are likely experiencing — bullet dots take the system's
          tier colour so the section reads as a continuation of the score. */}
      {likely.length > 0 && (
        <Box sx={styles.section}>
          <Box sx={styles.sectionTitle(theme)}>
            What you are likely experiencing
          </Box>
          <Box sx={styles.likelyCard(theme)}>
            {likely.map((item, i) =>
              aiActive ? (
                // AI-guided: keep the original always-expanded layout.
                <Box key={i} sx={styles.likelyItem}>
                  <Box sx={styles.likelyHeader}>
                    <ColoredDot color={TIER_COLOR(theme)[scoreCategory]} />
                    <Box component="span" sx={styles.likelyLabel(theme)}>
                      {item.label}
                    </Box>
                  </Box>
                  <Box component="p" sx={styles.likelyDescription(theme)}>
                    {item.description}
                  </Box>
                </Box>
              ) : (
                // Manual: collapsible accordion — title only until expanded.
                <Box key={i} sx={styles.likelyAccordion}>
                  <Box
                    sx={styles.likelyAccordionHeader}
                    onClick={() => toggleLikely(i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleLikely(i);
                      }
                    }}
                  >
                    <Box sx={styles.likelyHeader}>
                      <ColoredDot color={TIER_COLOR(theme)[scoreCategory]} />
                      <Box component="span" sx={styles.likelyLabel(theme)}>
                        {item.label}
                      </Box>
                    </Box>
                    <KeyboardArrowDownIcon
                      sx={styles.likelyChevron(theme, openLikely.has(i))}
                    />
                  </Box>
                  <Collapse in={openLikely.has(i)}>
                    <Box
                      component="p"
                      sx={[
                        styles.likelyDescription(theme),
                        styles.likelyDescriptionInset,
                      ]}
                    >
                      {item.description}
                    </Box>
                  </Collapse>
                </Box>
              )
            )}
          </Box>
        </Box>
      )}

      {/* 5. Single-test biomarker list. Historic comparison (2-bundle
          `TestHistoryTable`) was removed per product — to be redesigned
          from scratch later. */}
      <SingleTestSections system={system} />
    </Box>
  );
};

export default SystemDetailPanel;

/**
 * 1-test variant — PNOE metrics only (parent metric + nested breath
 * biomarkers in a gray inset). Blood Biomarkers were intentionally removed
 * from the consultation's deep-dive surface; they still live in the score
 * screen's SystemDetails for affiliates who need the full lab panel.
 *
 * Renders the SAME `MetricCard` the score screen's SystemDetails uses, with
 * the same data-derivation rules. Click handlers are no-ops in the
 * consultation (no side panel to open).
 */
const SingleTestSections: React.FC<{ system: any }> = ({ system }) => {
  const theme: Theme = useTheme();
  const { data } = useConsultation();
  // Same Redux slice the score screen reads. The biomarker list carries
  // long-form descriptions that override the per-test `dashBio.description`
  // when present — mirrors `SystemDetails.tsx`'s build exactly.
  const biomarkerList = useSelector(
    (state: RootStatePlatform) => state.platform.biomarkerList
  );

  // Mirror score screen's SystemDetails build for pnoeMetrics:
  // join system.breathBiomarkers with dashboard.metrics + dashboard.breathbiomarkers.
  const pnoeMetrics = useMemo(() => {
    return ((system.breathBiomarkers ?? []) as any[])
      .map((breathBio: any) => {
        const dashMetric = (data.dashboard?.metrics ?? []).find(
          (m: any) => m.id === breathBio.id
        );
        if (!dashMetric) return null;
        const biomarkers = ((breathBio.biomarkers ?? []) as any[])
          .map((nested: any) => {
            const dashBio: any = (data.dashboard?.breathbiomarkers ?? []).find(
              (b: any) => b.id === nested.id
            );
            if (!dashBio) return null;
            return {
              id: dashBio.id,
              label: dashBio.label,
              description: dashBio.description,
              value: dashBio.value,
              unit: dashBio.unit,
              score: dashBio.score,
              scoreCategory: dashBio.scoreCategory,
              values: dashBio.values,
            };
          })
          .filter(Boolean);
        return {
          id: dashMetric.id,
          name: dashMetric.label,
          score: dashMetric.score,
          scoreCategory: dashMetric.scoreCategory,
          value: dashMetric.value,
          unit: dashMetric.unit,
          biomarkers,
        };
      })
      .filter(Boolean) as any[];
  }, [system, data]);

  // Mirror score screen's SystemDetails build for blood biomarkers:
  // join system.bloodBiomarkers with dashboard.bloodbiomarkers, group by
  // `kind`, and look up long-form descriptions in the global biomarker list.
  // The render uses the same `BloodBiomarkerCard` the score screen uses —
  // omitting the side-panel click/highlight props since the consultation
  // has no side panel.
  const bloodBiomarkersGrouped = useMemo(() => {
    const grouped: Record<number, any[]> = {};
    ((system.bloodBiomarkers ?? []) as any[]).forEach((bb: any) => {
      const dashBio: any = (data.dashboard?.bloodbiomarkers ?? []).find(
        (d: any) => d.id === bb.id
      );
      if (!dashBio) return;
      const kind = dashBio.kind as number;
      // Only render blood biomarkers whose test kind is 4 (Body composition).
      if (kind !== BLOOD_BIOMARKER_TEST_KIND) return;
      if (!grouped[kind]) grouped[kind] = [];
      const biomarkerInfo = (biomarkerList ?? []).find(
        (b: any) => b.id === dashBio.id
      );
      grouped[kind].push({
        id: dashBio.id,
        label: dashBio.shortname || dashBio.label,
        fullName: dashBio.label,
        description: biomarkerInfo?.text || dashBio.description,
        value: dashBio.value,
        unit: dashBio.unit,
        score: dashBio.score,
        scoreCategory: dashBio.scoreCategory,
        values: dashBio.values,
        influencedBy: dashBio.influencedBy,
        affectsThis: dashBio.affectsThis,
      });
    });
    return grouped;
  }, [system, data, biomarkerList]);

  const hasBloodBiomarkers = Object.keys(bloodBiomarkersGrouped).length > 0;

  if (pnoeMetrics.length === 0 && !hasBloodBiomarkers) return null;

  return (
    <>
      {pnoeMetrics.length > 0 && (
        <Box sx={styles.section}>
          <Box sx={styles.sectionTitleBlock}>
            <Box sx={styles.sectionTitle(theme)}>PNOE metrics</Box>
            <Box sx={styles.sectionSubtitle(theme)}>
              Calculated by breath biomarkers
            </Box>
          </Box>
          <Box sx={styles.metricsList}>
            {pnoeMetrics.map((metric: any, idx: number) => (
              <MetricCard key={idx} metric={metric} theme={theme} />
            ))}
          </Box>
        </Box>
      )}
      {hasBloodBiomarkers && (
        <Box sx={styles.section}>
          <Box sx={styles.sectionTitle(theme)}>Biomarkers</Box>
          {Object.entries(bloodBiomarkersGrouped).map(([kind, biomarkers]) => (
            <Box key={kind} sx={styles.bloodKindGroup}>
              <Box sx={styles.sectionSubtitle(theme)}>
                {enPrecisionTestKind[Number(kind)] || "Other"}
              </Box>
              {biomarkers.map((bio: any, idx: number) => (
                <BloodBiomarkerCard key={idx} biomarker={bio} theme={theme} />
              ))}
            </Box>
          ))}
        </Box>
      )}
    </>
  );
};

const styles = {
  panel: (theme: Theme) => ({
    backgroundColor: `${theme.colors_ds.NEUTRAL_BG_MAIN}59`,
    border: `1px solid rgba(0,0,0,0.05)`,
    borderRadius: "16px",
    padding: "16px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "24px",
    minHeight: "100%",
    overflow: "auto",
    boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.05)",
  }),
  titleSection: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    height: "32px",
  },
  title: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    lineHeight: "28px",
    letterSpacing: "-0.4395px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  scoreText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "18px",
    lineHeight: "32px",
    letterSpacing: "0.07px",
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
  }),
  subtle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  divider: (theme: Theme) => ({
    height: "1px",
    minHeight: "1px",
    width: "100%",
    flexShrink: 0,
    backgroundColor: theme.colors_ds.NEUTRAL_BORDER_WEAK,
  }),
  section: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
    width: "100%",
  },
  sectionTitleBlock: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "4px",
  },
  sectionTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.4",
    letterSpacing: "-0.28px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  sectionSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),

  // Recommended services
  serviceChips: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "8px",
  },
  serviceChip: (theme: Theme) => ({
    backgroundColor: theme.colorTokens.P[10],
    borderRadius: "8px",
    padding: "8px",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
  }),

  // Likely experiencing
  likelyCard: (theme: Theme) => ({
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    padding: "8px 12px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  }),
  likelyItem: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
  likelyHeader: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  likelyLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  likelyDescription: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  // Accordion variant (manual mode) — collapsed by default, click the header
  // to reveal the description. Multiple can be open at once.
  likelyAccordion: {
    display: "flex",
    flexDirection: "column" as const,
  },
  likelyAccordionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    cursor: "pointer",
  },
  likelyChevron: (theme: Theme, open: boolean) => ({
    fontSize: "18px",
    color: theme.colors_ds.NEUTRAL_ICON_WEAK,
    transition: "transform 0.2s ease",
    transform: open ? "rotate(180deg)" : "rotate(0deg)",
  }),
  likelyDescriptionInset: {
    paddingTop: "8px",
  },

  // PNOE metrics list — gap matches the score screen's `metricsSection`.
  metricsList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },

  // Blood biomarkers — one group per `kind` (Glucose, Lipids, etc.).
  // Matches the score screen's `bloodKindGroup`: subtitle on top, stacked
  // `BloodBiomarkerCard` rows below.
  bloodKindGroup: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "8px",
  },
};
